// Takeoff drawing helpers: angle snapping, point snapping, unit conversion

export interface SnapPoint {
  x: number;
  y: number;
}

export interface SnapGuide {
  axis: "x" | "y";
  value: number; // absolute canvas px
}

/**
 * Snap cursor to nearest 45° angle relative to last point.
 * Operates on relative coords but needs canvas dims for correct aspect ratio.
 */
export function snapToAngle(
  last: SnapPoint,
  cursor: SnapPoint,
  canvasW: number,
  canvasH: number
): SnapPoint {
  const lx = last.x * canvasW,
    ly = last.y * canvasH;
  const cx = cursor.x * canvasW,
    cy = cursor.y * canvasH;
  const dx = cx - lx,
    dy = cy - ly;
  const angle = Math.atan2(dy, dx);
  const snapped = Math.round(angle / (Math.PI / 4)) * (Math.PI / 4);
  const dist = Math.sqrt(dx * dx + dy * dy);
  return {
    x: (lx + Math.cos(snapped) * dist) / canvasW,
    y: (ly + Math.sin(snapped) * dist) / canvasH,
  };
}

/**
 * Collect all annotation endpoints (for snap-to-point guides).
 */
export function collectEndpoints(annotations: any[]): SnapPoint[] {
  const pts: SnapPoint[] = [];
  annotations.forEach((ann) => {
    const geo = ann.geometry;
    if (geo?.point) pts.push(geo.point);
    if (geo?.points)
      geo.points.forEach((p: any) => pts.push({ x: p.x, y: p.y }));
  });
  return pts;
}

/**
 * Snap cursor to nearby existing annotation endpoints.
 * Returns snapped position and guide lines to draw.
 */
export function snapToNearbyPoint(
  cursor: SnapPoint,
  endpoints: SnapPoint[],
  canvasW: number,
  canvasH: number,
  threshold = 8
): { snapped: SnapPoint; guides: SnapGuide[] } {
  let snapped = { ...cursor };
  const guides: SnapGuide[] = [];

  let minDist = Infinity;
  let nearestPt: SnapPoint | null = null;

  endpoints.forEach((ep) => {
    const dx = Math.abs(cursor.x * canvasW - ep.x * canvasW);
    const dy = Math.abs(cursor.y * canvasH - ep.y * canvasH);
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist < threshold && dist < minDist) {
      minDist = dist;
      nearestPt = ep;
    }

    if (dx < threshold) {
      guides.push({ axis: "x", value: ep.x * canvasW });
    }
    if (dy < threshold) {
      guides.push({ axis: "y", value: ep.y * canvasH });
    }
  });

  if (nearestPt) {
    snapped = { ...nearestPt };
  } else {
    // Snap to axis-aligned guides individually
    endpoints.forEach((ep) => {
      const dx = Math.abs(cursor.x * canvasW - ep.x * canvasW);
      const dy = Math.abs(cursor.y * canvasH - ep.y * canvasH);
      if (dx < threshold) snapped.x = ep.x;
      if (dy < threshold) snapped.y = ep.y;
    });
  }

  return { snapped, guides };
}

// ─── Unit conversion ───

const CONVERSION_FROM_FT: Record<string, number> = {
  ft: 1,
  in: 12,
  yd: 1 / 3,
  m: 0.3048,
  cm: 30.48,
  mm: 304.8,
};

export const LINEAR_UNITS = ["ft", "in", "yd", "m", "cm", "mm"] as const;

export function convertLinear(
  value: number,
  fromUnit: string,
  toUnit: string
): number {
  if (fromUnit === toUnit || !value) return value;
  const from = CONVERSION_FROM_FT[fromUnit] ?? 1;
  const to = CONVERSION_FROM_FT[toUnit] ?? 1;
  return (value / from) * to;
}

export function convertArea(
  value: number,
  fromUnit: string,
  toUnit: string
): number {
  if (fromUnit === toUnit || !value) return value;
  const from = CONVERSION_FROM_FT[fromUnit] ?? 1;
  const to = CONVERSION_FROM_FT[toUnit] ?? 1;
  return (value / (from * from)) * (to * to);
}

export function linearUnitLabel(unit: string): string {
  return unit;
}

export function areaUnitLabel(unit: string): string {
  return `sq ${unit}`;
}

/**
 * Parse imperial feet-inches notation into decimal feet.
 * Supports: 5'11", 5' 11", 5', 11", 10'6, plain decimals like 5.5
 * Returns null for unparseable input.
 */
export function parseFeetInches(input: string): number | null {
  if (!input || !input.trim()) return null;
  const s = input.trim();

  // Plain number passthrough
  const plain = parseFloat(s);
  if (/^[\d.]+$/.test(s) && !isNaN(plain)) return plain;

  let feet = 0;
  let inches = 0;
  let matched = false;

  // Pattern: feet'inches" or feet' inches" or feet'inches (no closing quote)
  const feetInchesRe = /^(\d+(?:\.\d+)?)\s*['']\s*(\d+(?:\.\d+)?)\s*["""]?\s*$/;
  const m1 = s.match(feetInchesRe);
  if (m1) {
    feet = parseFloat(m1[1]);
    inches = parseFloat(m1[2]);
    matched = true;
  }

  // Pattern: feet' only (no inches)
  if (!matched) {
    const feetOnlyRe = /^(\d+(?:\.\d+)?)\s*['']\s*$/;
    const m2 = s.match(feetOnlyRe);
    if (m2) {
      feet = parseFloat(m2[1]);
      matched = true;
    }
  }

  // Pattern: inches" only (no feet)
  if (!matched) {
    const inchesOnlyRe = /^(\d+(?:\.\d+)?)\s*["""]$/;
    const m3 = s.match(inchesOnlyRe);
    if (m3) {
      inches = parseFloat(m3[1]);
      matched = true;
    }
  }

  if (!matched) return null;

  const result = feet + inches / 12;
  return isNaN(result) ? null : result;
}
